import React, { FC, Fragment, useEffect, useState } from 'react';

import { Box, Divider, Typography } from '@mui/material';
import { useFormik } from 'formik';

import { ReactComponent as FavouriteActive } from '@pharmaplan/common/assets/icons/favourtieActive.svg';
import { ReactComponent as FavouriteNotActive } from '@pharmaplan/common/assets/icons/favouriteNotActive.svg';
import {
  Language,
  preLoginLang,
  PublicRatingType,
  resetStatus,
  successSelector,
} from '@pharmaplan/common';

import { showSuccess } from '../Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import RatingQuestions from '../MyReplacements/PharmacistRating/RatingQuestions';
import usePublicRating from '../../hooks/Rating/usePublicRating';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import CustomRatingStar from '../common/CustomRatingStar';
import useSelfService from '../../hooks/useSelfService';
import CustomTextField from '../common/CustomTextField';
import { Constants } from '../../helpers/Constants';
import CustomButton from '../common/CustomButton';
import strings from '../../localization';

import validator from './validator';
import styles from './styles';

interface IPublicRating {
  ratingType: PublicRatingType;
}

const PublicRating: FC<IPublicRating> = ({ ratingType }) => {
  const dispatch = useAppDispatch();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const { isSelfService } = useSelfService();
  const [ratingLevel, setRatingLevel] = useState<{ [key: string]: number }>({});
  const urlParams = new URLSearchParams(window.location.search);

  const preLogin = useAppSelector(preLoginLang);
  const isEN = preLogin === Language.en;

  const pharmacistId = urlParams.get('sid') ?? '';
  const pharmacyId = urlParams.get('cid') ?? '';
  const bookingId = urlParams.get('bid') ?? '';

  const {
    getAPI,
    ratingItems,
    ratingItemId,
    name,
    title,
    questions,
    questionsIdProperty,
    handleSubmit,
    maxRatedItemsPossible,
    successAction,
    displayDetailForm,
  } = usePublicRating(
    ratingType,
    ratingLevel,
    bookingId,
    pharmacistId,
    pharmacyId,
  );

  const success = useAppSelector((state) =>
    successSelector([successAction], state));

  useEffect(() => {
    dispatch(
      getAPI({
        pharmacistId,
        pharmacyId,
        bookingId,
        locale: preLogin as Language,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik({
    initialValues: { comment: '', favourite: false, numberOfRatedItems: 0 },
    onSubmit: handleSubmit,
    validationSchema: validator(maxRatedItemsPossible ?? 0),
  });

  const handleFavClick = () => {
    formik.setFieldValue('favourite', !formik.values.favourite);
  };

  useEffect(() => {
    formik.setFieldValue('numberOfRatedItems', Object.keys(ratingLevel).length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratingLevel]);

  useEffect(() => {
    if (success) {
      showSuccess(dispatch, strings.ratingSuccessful);
      dispatch(resetStatus([successAction]));
      setDisableSubmit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.subContainer}>
        <Typography sx={styles.title}>{title}</Typography>
        <Box sx={styles.ratingContainer}>
          <Box sx={styles.nameContainer}>
            <Typography sx={styles.name}>
              [
              {name}
              ]
            </Typography>
            <CustomButton
              variant={Constants.variant.outlined}
              label={strings.favourite}
              onClick={handleFavClick}
              startIcon={
                formik.values.favourite ? (
                  <FavouriteActive />
                ) : (
                  <FavouriteNotActive />
                )
              }
            />
          </Box>

          {displayDetailForm
            && ratingItems?.map((item: any) => {
              const { englishName, frenchName } = item ?? {};
              const id = item[ratingItemId as keyof typeof item];

              return (
                <Fragment key={id}>
                  <CustomRatingStar
                    // disabled={!!doesDataExist}
                    key={id}
                    text={isEN ? englishName : frenchName}
                    count={5}
                    ratingLevel={ratingLevel[id] ?? 0}
                    handleClick={(starIndex: number) =>
                      setRatingLevel((prev: any) =>
                        ({
                          ...prev,
                          [id]: starIndex,
                        }))}
                  />
                  <Divider />
                </Fragment>
              );
            })}
          {isSelfService && (
            <RatingQuestions
              professionalRatingQuestions={questions}
              idType={questionsIdProperty}
              formik={formik}
              customLanguage={preLogin as Language}
            />
          )}

          <CustomTextField
            customClass={styles.commentField}
            label={strings.comment}
            name="comment"
            id="comment"
            placeholder={strings.formatString(
              strings.pleaseEnterFormat,
              strings.comment,
            )}
            formik={formik}
            multiline
          />

          {formik.touched.numberOfRatedItems
            && formik.errors.numberOfRatedItems && (
              <Typography sx={styles.error}>
                {strings.pleaseSelectRating}
              </Typography>
          )}
          <Box sx={styles.buttonContainer}>
            <CustomButton
              label={strings.submit}
              onClick={formik.handleSubmit}
              disabled={disableSubmit}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PublicRating;
